import React, { createContext, useContext, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { nationalHolidayService } from '../services/nationalHolidayService';
import { HolidayInstance } from '@timesheet/schemas';

interface NationalHolidaysContextType {
  holidays: HolidayInstance[];
  loading: boolean;
  error: string | null;
  refreshHolidays: () => Promise<void>;
  isHoliday: (date: Date) => HolidayInstance | undefined;
  isBlockingHoliday: (date: Date) => HolidayInstance | undefined;
}

const NationalHolidaysContext = createContext<NationalHolidaysContextType | undefined>(undefined);

export const NationalHolidaysProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [holidays, setHolidays] = useState<HolidayInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const data = await nationalHolidayService.getAll(new Date().getFullYear());

      setHolidays(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch holidays');
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const isHoliday = (date: Date) => {
    if (!Array.isArray(holidays)) {
      return undefined;
    }
    const dateStr = format(date, 'yyyy-MM-dd');

    const found = holidays.find(
      (holiday) => !holiday.isCompanyClosing && format(new Date(holiday.date), 'yyyy-MM-dd') === dateStr,
    );
    return found;
  };

  const isBlockingHoliday = (date: Date) => {
    const holiday = isHoliday(date);
    if (!holiday || holiday.allowTimesheetEntry) {
      return undefined;
    }
    return holiday;
  };

  return (
    <NationalHolidaysContext.Provider
      value={{
        holidays,
        loading,
        error,
        refreshHolidays: fetchHolidays,
        isHoliday,
        isBlockingHoliday,
      }}
    >
      {children}
    </NationalHolidaysContext.Provider>
  );
};

export const useNationalHolidays = () => {
  const context = useContext(NationalHolidaysContext);
  if (context === undefined) {
    throw new Error('useNationalHolidays must be used within a NationalHolidaysProvider');
  }
  return context;
};
